/**
 * v3 API - Bot 相关接口
 *
 * 对应后端路由:
 *   prefix: /api/bots
 *   实现文件: api/routes/bot/
 *
 * @module services/api/v3/bots
 */

import request from './client.js';

// ============================================================================
// 列表与统计
// ============================================================================

/**
 * 获取所有 Bot 列表
 *
 * GET /api/bots
 *
 * @returns {Promise<Array<object>>} Bot 列表
 */
export async function fetchBots() {
  const data = await request('/bots');
  return data.bots || [];
}

/**
 * 获取单个 Bot 详情
 *
 * GET /api/bots/:id
 *
 * @param {string} id - Bot ID
 * @returns {Promise<object>} Bot 详细信息
 */
export async function fetchBot(id) {
  return request(`/bots/${id}`);
}

/**
 * 获取在线 Bot 数量
 *
 * GET /api/bots/online-count
 *
 * @returns {Promise<number>}
 */
export async function fetchOnlineCount() {
  const data = await request('/bots/online-count');
  return data.count || 0;
}

/**
 * 获取 Bot 总数
 *
 * GET /api/bots/total-count
 *
 * @returns {Promise<number>}
 */
export async function fetchTotalCount() {
  const data = await request('/bots/total-count');
  return data.count || 0;
}

/**
 * 同时获取在线数和总数
 *
 * 并行请求 online-count 和 total-count 以提高效率。
 *
 * @returns {Promise<{ onlineCount: number, totalCount: number }>}
 */
export async function fetchStats() {
  const [onlineCount, totalCount] = await Promise.all([
    fetchOnlineCount(),
    fetchTotalCount(),
  ]);
  return { onlineCount, totalCount };
}

// ============================================================================
// 生命周期管理
// ============================================================================

/**
 * 创建新 Bot
 *
 * POST /api/bots
 *
 * @param {object} params
 * @param {string} params.name     - Minecraft 用户名
 * @param {string} params.host     - 服务器地址
 * @param {number} params.port     - 服务器端口
 * @param {string} [params.password] - 服务器密码（可选）
 * @returns {Promise<object>} 创建结果，含 bot_id 等
 */
export async function createBot({ name, host, port, password }) {
  const body = { name, host, port };
  if (password !== undefined && password !== '') {
    body.password = password;
  }
  return request('/bots', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * 删除 Bot
 *
 * DELETE /api/bots/:id
 *
 * @param {string}  id     - Bot ID
 * @param {boolean} [force=false] - 是否强制删除
 * @returns {Promise<object>}
 */
export async function deleteBot(id, force = false) {
  return request(`/bots/${id}${force ? '?force=true' : ''}`, {
    method: 'DELETE',
  });
}

/**
 * 启动 Bot
 *
 * POST /api/bots/:id/start
 *
 * @param {string}  id     - Bot ID
 * @param {boolean} [force=false] - 是否强制启动
 * @returns {Promise<object>}
 */
export async function startBot(id, force = false) {
  return request(`/bots/${id}/start`, {
    method: 'POST',
    body: force ? JSON.stringify({ force: true }) : undefined,
  });
}

/**
 * 停止 Bot
 *
 * POST /api/bots/:id/stop
 *
 * @param {string} id - Bot ID
 * @returns {Promise<object>}
 */
export async function stopBot(id) {
  return request(`/bots/${id}/stop`, {
    method: 'POST',
  });
}

// ============================================================================
// 命令与动作
// ============================================================================

/**
 * 向 Bot 发送聊天命令
 *
 * POST /api/bots/:id/command
 *
 * @param {string} id      - Bot ID
 * @param {string} command - 聊天命令文本
 * @returns {Promise<object>}
 */
export async function sendCommand(id, command) {
  return request(`/bots/${id}/command`, {
    method: 'POST',
    body: JSON.stringify({ command }),
  });
}

/**
 * 执行 Bot 预设动作
 *
 * POST /api/bots/:id/action
 *
 * @param {string}              id      - Bot ID
 * @param {string}              action  - 动作名称
 * @param {Record<string, any>} [params] - 动作参数（可选，按动作模板）
 * @returns {Promise<object>}
 */
export async function executeAction(id, action, params) {
  const body = { action };
  if (params !== undefined && Object.keys(params).length > 0) {
    body.params = params;
  }
  return request(`/bots/${id}/action`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * 获取可用动作模板列表
 *
 * GET /api/bots/actions
 *
 * @returns {Promise<Array<{ action: string, description: string, params: Array }>>}
 */
export async function fetchActions() {
  return request('/bots/actions');
}

// ============================================================================
// 日志与聊天
// ============================================================================

/**
 * 获取 Bot 日志
 *
 * GET /api/bots/:id/logs
 *
 * @param {string}  id     - Bot ID
 * @param {number}  [lines=50] - 获取的行数（1-1000）
 * @returns {Promise<Array<string>>}
 */
export async function fetchLogs(id, lines = 50) {
  const data = await request(`/bots/${id}/logs?lines=${lines}`);
  return data.logs || [];
}

/**
 * 获取 Bot 聊天记录
 *
 * GET /api/bots/:id/chats
 *
 * @param {string}  id     - Bot ID
 * @param {number}  [lines=50] - 获取的行数（1-1000）
 * @returns {Promise<Array<string>>}
 */
export async function fetchChats(id, lines = 50) {
  const data = await request(`/bots/${id}/chats?lines=${lines}`);
  return data.chats || [];
}

// ============================================================================
// 配置管理
// ============================================================================

/**
 * 获取 Bot 配置（不含密码）
 *
 * GET /api/bots/:id/config
 *
 * @param {string} id - Bot ID
 * @returns {Promise<object>} Bot 配置对象（无 password 字段）
 */
export async function fetchBotConfig(id) {
  return request(`/bots/${id}/config`);
}

/**
 * 部分更新 Bot 配置
 *
 * PATCH /api/bots/:id/config
 *
 * 可更新字段:
 *   name, host, port, password,
 *   autoReconnect, maxReconnect, reconnectInterval, commandPrefix
 *
 * @param {string} id   - Bot ID
 * @param {object} data - 要更新的配置字段
 * @returns {Promise<object>} 更新后的完整配置（无 password）
 */
export async function updateBotConfig(id, data) {
  return request(`/bots/${id}/config`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}
